import { useCallback } from 'react';

import type { AdvancedSearchResultInteractedMetadata } from '../../../common/utils/events/advanced-search-result-interacted';
import {
	onSummarizeInChatButtonClicked,
	type SummarizeInChatButtonClickedArgs,
} from '../../../common/utils/events/summarize-in-chat-button-clicked';
import { getExtensionData } from '../../../common/utils/relay/get-extension-data';
import { useAnalytics, useCommonAndPrivateAttributes } from '../../analytics';
import type { SharedEventAttributes } from '../../analytics/prepared-result-attributes';
import type { AnalyticsEventCallbacks } from '../../analytics/result-analytics-callbacks';

interface FireSummarizeInChatButtonClickedProps {
	location: SummarizeInChatButtonClickedArgs['location'];
	metadata: AdvancedSearchResultInteractedMetadata;
	position?: number;
}
/**
 * @returns a callback to fire an event when the result
 * is clicked by the user.
 */

export const useFireSummarizeInChatButtonClickedWithAttributes = (
	sharedEventAttributes: SharedEventAttributes,
	eventCallbacks: AnalyticsEventCallbacks,
	summarizeEventAttributes: FireSummarizeInChatButtonClickedProps,
): (() => void) => {
	const { location, metadata, position } = summarizeEventAttributes;
	const { commonAttributes, nonPrivacySafeAttributes } = sharedEventAttributes;
	const { fireAnalyticsEvent } = eventCallbacks;
	return useCallback(() => {
		fireAnalyticsEvent(
			onSummarizeInChatButtonClicked({
				commonAttributes: {
					...commonAttributes,
					...getExtensionData(commonAttributes.isCQL),
				},
				nonPrivacySafeAttributes,
				location,
				metadata,
				position,
			}),
		);
	}, [
		fireAnalyticsEvent,
		commonAttributes,
		nonPrivacySafeAttributes,
		location,
		metadata,
		position,
	]);
};

export const useFireSummarizeInChatButtonClicked = (
	summarizeEventAttributes: FireSummarizeInChatButtonClickedProps,
): (() => void) => {
	const sharedEventAttributes = useCommonAndPrivateAttributes();
	const { fireAnalyticsEvent } = useAnalytics();
	return useFireSummarizeInChatButtonClickedWithAttributes(
		sharedEventAttributes,
		{ fireAnalyticsEvent },
		summarizeEventAttributes,
	);
};
